import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ProjectListEntry } from '../../lib/api';
import { formatDateTime, projectDisplayName } from '../../lib/format';

interface GroupedProjects {
  tops: ProjectListEntry[];
  childrenByParentId: Map<string, ProjectListEntry[]>;
}

// Most recent activity timestamp for sorting. Falls back to updated_at so
// projects without any block still get a stable, sensible position.
function activityKey(p: ProjectListEntry): string {
  return p.last_activity_at ?? p.updated_at;
}

// Descending by recent activity (most recent first).
function byRecentActivity(a: ProjectListEntry, b: ProjectListEntry): number {
  return activityKey(b).localeCompare(activityKey(a));
}

// Splits the project list into top-level entries (no visible parent) and a
// children-by-parent map. Children whose `parent_project_id` doesn't match
// any project the user can see are promoted to the top level rather than
// hidden, so nothing is silently dropped.
function groupByParent(projects: ProjectListEntry[]): GroupedProjects {
  const ids = new Set(projects.map((p) => p.id));
  const childrenByParentId = new Map<string, ProjectListEntry[]>();
  const tops: ProjectListEntry[] = [];

  for (const p of projects) {
    if (p.parent_project_id && ids.has(p.parent_project_id)) {
      const arr = childrenByParentId.get(p.parent_project_id) ?? [];
      arr.push(p);
      childrenByParentId.set(p.parent_project_id, arr);
    } else {
      tops.push(p);
    }
  }

  for (const arr of childrenByParentId.values()) {
    arr.sort(byRecentActivity);
  }
  // A parent's effective activity includes its children's, so a group whose
  // sub-project was just worked on bubbles to the top.
  const effective = new Map<string, string>();
  for (const p of tops) {
    const children = childrenByParentId.get(p.id) ?? [];
    const keys = [activityKey(p), ...children.map(activityKey)];
    effective.set(p.id, keys.reduce((max, k) => (k > max ? k : max)));
  }
  tops.sort((a, b) => (effective.get(b.id) ?? '').localeCompare(effective.get(a.id) ?? ''));
  return { tops, childrenByParentId };
}

// Builds a tag → projects index. Multi-tag projects appear under each of
// their tags; projects without any tag fall into a synthetic 'untagged'
// bucket so nothing is silently hidden when this view is active.
function groupByTag(projects: ProjectListEntry[]): Map<string, ProjectListEntry[]> {
  const byTag = new Map<string, ProjectListEntry[]>();
  for (const p of projects) {
    if (p.tags.length === 0) {
      const arr = byTag.get('untagged') ?? [];
      arr.push(p);
      byTag.set('untagged', arr);
      continue;
    }
    for (const tag of p.tags) {
      const arr = byTag.get(tag) ?? [];
      arr.push(p);
      byTag.set(tag, arr);
    }
  }
  for (const arr of byTag.values()) {
    arr.sort(byRecentActivity);
  }
  return byTag;
}

type GroupMode = 'parent' | 'tag';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; projects: ProjectListEntry[] };

export function ProjectsList() {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [mode, setMode] = useState<GroupMode>('parent');
  const grouped = useMemo<GroupedProjects | null>(() => {
    if (state.kind !== 'ready') return null;
    return groupByParent(state.projects);
  }, [state]);
  const tagGroups = useMemo<Map<string, ProjectListEntry[]> | null>(() => {
    if (state.kind !== 'ready') return null;
    return groupByTag(state.projects);
  }, [state]);

  useEffect(() => {
    let cancelled = false;
    api
      .listProjects()
      .then((projects) => {
        if (!cancelled) setState({ kind: 'ready', projects });
      })
      .catch((err) => {
        if (!cancelled) setState({ kind: 'error', message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return (
      <div className="py-24 flex items-center justify-center">
        <span className="mono-label text-black/50">loading projects…</span>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="py-24">
        <p className="font-display text-body text-black/70">
          Could not load projects: {state.message}
        </p>
      </div>
    );
  }

  if (state.projects.length === 0) {
    return (
      <div className="py-24 text-center">
        <div className="mono-label text-black/50 mb-4">no projects yet</div>
        <h2 className="font-display text-section font-medium tracking-section text-black mb-4">
          Nothing synced yet
        </h2>
        <p className="font-display text-body-lg text-black/60 max-w-md mx-auto tracking-body">
          Make sure your local service is running with cloud sync enabled, then
          track some time. Projects show up here as they land in D1.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono-label text-black/50 mb-4">dashboard</div>
          <h1 className="font-display text-section font-medium tracking-section text-black">
            Projects
          </h1>
          <p className="mt-3 font-display text-body-lg text-black/60 tracking-body">
            {state.projects.length} synced from your devices. Click to see blocks, summary,
            and per-agent breakdown.
          </p>
        </div>
        <GroupModeToggle mode={mode} onChange={setMode} />
      </div>

      {mode === 'parent' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {grouped?.tops.map((project) => {
            const children = grouped.childrenByParentId.get(project.id) ?? [];
            return (
              <ProjectCard key={project.project_key} project={project} children={children} />
            );
          })}
        </div>
      )}

      {mode === 'tag' && tagGroups && <TagSections tagGroups={tagGroups} />}
    </div>
  );
}

function GroupModeToggle({
  mode,
  onChange,
}: {
  mode: GroupMode;
  onChange: (m: GroupMode) => void;
}) {
  const options: Array<{ value: GroupMode; label: string }> = [
    { value: 'parent', label: 'By parent' },
    { value: 'tag', label: 'By tag' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Group projects by"
      className="inline-flex p-1 rounded-full bg-glass-dark border border-border-light"
    >
      {options.map((opt) => {
        const active = opt.value === mode;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={`mono-label px-4 py-1.5 rounded-full transition-colors ${
              active
                ? 'bg-midnight text-white'
                : 'text-black/60 hover:text-black'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// Renders one section per tag. Tags are ordered by descending project count
// (so heaviest groups appear first), with 'untagged' always last.
function TagSections({ tagGroups }: { tagGroups: Map<string, ProjectListEntry[]> }) {
  const orderedTags = Array.from(tagGroups.keys()).sort((a, b) => {
    if (a === 'untagged') return 1;
    if (b === 'untagged') return -1;
    const countDiff = (tagGroups.get(b)?.length ?? 0) - (tagGroups.get(a)?.length ?? 0);
    if (countDiff !== 0) return countDiff;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-12">
      {orderedTags.map((tag) => {
        const projects = tagGroups.get(tag) ?? [];
        const isUntagged = tag === 'untagged';
        return (
          <section key={tag}>
            <div className="flex items-baseline gap-3 mb-4">
              <h2
                className={`font-display text-sub font-medium tracking-sub ${
                  isUntagged ? 'text-black/50' : 'text-black'
                }`}
              >
                {isUntagged ? 'Untagged' : `#${tag}`}
              </h2>
              <span className="mono-label text-black/40">
                {projects.length} {projects.length === 1 ? 'project' : 'projects'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {projects.map((project) => (
                <ProjectCard key={project.project_key} project={project} children={[]} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ProjectCard({
  project,
  children,
}: {
  project: ProjectListEntry;
  children: ProjectListEntry[];
}) {
  return (
    <div className="group card p-5 hover:shadow-elevated-lg transition-shadow flex flex-col">
      <Link
        to={`/projects/${encodeURIComponent(project.project_key)}`}
        className="focus:outline-none focus:border-midnight"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="mono-label text-black/50">project</div>
          <div className="mono-label text-black/30">
            {formatDateTime(activityKey(project)).split(',')[0]}
          </div>
        </div>
        <h3 className="font-display text-sub font-medium tracking-sub text-black break-words leading-[1.1] group-hover:text-midnight transition-colors">
          {projectDisplayName(project)}
        </h3>
        <p className="mt-3 font-mono text-[11px] text-black/40 break-all leading-[1.4]">
          {project.project_key}
        </p>

        {(project.client_name || project.tags.length > 0) && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5">
            {project.client_name && (
              <span className="badge !bg-[#e8e4ff] !border-[#bdbbff] !text-midnight">
                {project.client_name}
              </span>
            )}
            {project.tags.map((tag) => (
              <span key={tag} className="badge">
                {tag}
              </span>
            ))}
          </div>
        )}
      </Link>

      {children.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border-light">
          <div className="mono-label text-black/50 mb-2">
            sub-projects · {children.length}
          </div>
          <ul className="space-y-1">
            {children.map((c) => (
              <li key={c.project_key}>
                <Link
                  to={`/projects/${encodeURIComponent(c.project_key)}`}
                  className="block font-mono text-[12px] text-black/70 hover:text-midnight transition-colors truncate"
                >
                  ↳ {projectDisplayName(c)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
